import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTable, faDownload, faFilter, faSearch } from '@fortawesome/free-solid-svg-icons';

const WaterQualityTableAdmin = () => {
    const [farms, setFarms] = useState([]);
    const [selectedFarm, setSelectedFarm] = useState('');
    const [ponds, setPonds] = useState([]);
    const [selectedPond, setSelectedPond] = useState('');
    const [sensorData, setSensorData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [sensors, setSensors] = useState([]);
    const [selectedSensors, setSelectedSensors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('time');
    const [sortDirection, setSortDirection] = useState('desc');
    
    const startOfcurrentDate = new Date();
    const endOfCurrentDate = new Date();
    startOfcurrentDate.setHours(0, 0, 0, 0);
    endOfCurrentDate.setHours(23, 59, 59, 999);
    const [startDate, setStartDate] = useState(startOfcurrentDate);
    const [endDate, setEndDate] = useState(endOfCurrentDate);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [pagination, setPagination] = useState({
        total: 0,
        currentPage: 1,
        totalPages: 1,
    });
    const [pageSize, setPageSize] = useState(50);
    const [itemsPerPageOptions] = useState([10, 25, 50, 100, 200]);
    const { t } = useTranslation();
    const [districts, setDistricts] = useState([]);
    const [ngos, setNgos] = useState([]);
    const [filterNgo, setFilterNgo] = useState('');
    const [filterDistrict, setFilterDistrict] = useState('');
    const [filterSubdistrict, setFilterSubdistrict] = useState('');
    const [filterSubdistricts, setFilterSubdistricts] = useState([]);

    useEffect(() => {
        fetchFarms();
    }, []);

    useEffect(() => {
        setSelectedFarm('');
    }, [filterDistrict, filterSubdistrict]);

    useEffect(() => {
        const fetchDistricts = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/districts`);
                setDistricts(response.data);
            } catch (err) {
                console.error('Failed to fetch districts', err);
            }
        };
        const fetchNgos = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/ngos`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setNgos(response.data);
            } catch (error) {
                console.error('Failed to fetch NGOs:', error);
            }
        };
        fetchNgos();
        fetchDistricts();
    }, []);

    const fetchPonds = async (farmId) => {
        const token = localStorage.getItem('token');
        axios.get(`${process.env.REACT_APP_BASE_URL}/api/pond/farm/${farmId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }).then((response) => {
            setPonds(response.data);
        }).catch((error) => {
            Swal.fire({
                icon: 'error',
                title: t('failed_to_fetch_ponds') || 'Failed to fetch ponds',
                text: error.response?.data?.message || 'An error occurred while fetching ponds.',
            });
        });
    };

    const fetchFarms = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/farm/all/list`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setFarms(response.data.farms);
        } catch (error) {
            Swal.fire('Error', 'Failed to fetch farms.', 'error');
        }
    };

    const handleFarmSelect = (e) => {
        setSelectedPond('');
        setSensorData([]);
        setPonds([]);
        const farmId = e.target.value;
        setSelectedFarm(farmId);
        if(farmId){
            fetchPonds(farmId);
        }
    };

    const fetchSensorData = (pondId, sensorIds = null, page = 1, recurring = false) => {
        const token = localStorage.getItem('token');
        setLoading(true);
        let query = `?page=${page}&limit=${pageSize}`;
        if (startDate) query += `&startDate=${startDate.toISOString()}`;
        if (endDate) query += `&endDate=${endDate.toISOString()}`;
        if (sensorIds) query += `&sensor_ids=${sensorIds}`;

        axios
            .get(`${process.env.REACT_APP_BASE_URL}/api/pond/${pondId}/sensordata${query}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
            .then((response) => {
                if(recurring){
                    setSensorData((prevData) => [...prevData, ...response.data.data]);
                } else {
                    setSensorData(response.data.data);
                }
                setPagination({
                    total: response.data.pagination.total,
                    currentPage: response.data.pagination.currentPage,
                    totalPages: response.data.pagination.totalPages,
                });
                setLoading(false);
            })
            .catch((error) => {
                Swal.fire({
                    icon: 'error',
                    title: t('failed_to_fetch_sensor_data') || 'Failed to fetch sensor data',
                    text: error.response?.data?.message || 'An error occurred while fetching sensor data.',
                });
                setLoading(false);
            });
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.totalPages && page !== pagination.currentPage) {
            const sensorIds = selectedSensors.map(sensor => sensor.value).join(',');
            fetchSensorData(selectedPond, sensorIds || null, page);
        }
    };

    const getPaginationNumbers = () => {
        const pages = [];
        const current = pagination.currentPage;
        const total = pagination.totalPages;
        
        // Always show first page
        if (current > 3) pages.push(1);
        if (current > 4) pages.push('...');
        
        // Show current page and surrounding pages
        for (let i = Math.max(1, current - 2); i <= Math.min(total, current + 2); i++) {
            pages.push(i);
        }
        
        // Always show last page
        if (current < total - 3) pages.push('...');
        if (current < total - 2) pages.push(total);
        
        return pages;
    };

    const fetchSensors = (pondId) => {
        const token = localStorage.getItem('token');
        axios.get(`${process.env.REACT_APP_BASE_URL}/api/sensors/by-pond/${pondId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }).then((response) => {
            setSensors(response.data);
        }).catch((error) => {
            Swal.fire({
                icon: 'error',
                title: t('failed_to_fetch_sensors') || 'Failed to fetch sensors',
                text: error.response?.data?.message || 'An error occurred while fetching sensors.',
            });
        });
    };

    const handlePondChange = (e) => {
        const pondId = e.target.value;
        setSelectedPond(pondId);
        if (pondId) {
            fetchSensors(pondId);
            setSelectedSensors([]);
            setSensorData([]);
        } else {
            setSensorData([]);
            setSensors([]);
            setSelectedSensors([]);
            setPagination({
                total: 0,
                currentPage: 1,
                totalPages: 1,
            });
        }
    };

    const handleSensorChange = (selectedOptions) => {
        setSelectedSensors(selectedOptions || []);
    };

    const handleDateFilter = () => {
        if (selectedPond) {
            const sensorIds = selectedSensors.map(sensor => sensor.value).join(',');
            fetchSensorData(selectedPond, sensorIds || null, 1);
        }
    };

    const resetFilters = () => {
        const today = new Date();
        const startOfToday = new Date(today);
        const endOfToday = new Date(today);
        startOfToday.setHours(0, 0, 0, 0);
        endOfToday.setHours(23, 59, 59, 999);
        
        setStartDate(startOfToday);
        setEndDate(endOfToday);
        setSelectedMonth(today.getMonth());
        setSelectedYear(today.getFullYear());
        setSelectedSensors([]);
        setSensorData([]);
        setPagination({
            total: 0,
            currentPage: 1,
            totalPages: 1,
        });
        setFilterNgo('');
        setFilterDistrict('');
        setFilterSubdistrict('');
        setSelectedFarm('');
        setSelectedPond('');
    };

    // Filter and sort data
    useEffect(() => {
        let filtered = sensorData.filter(item =>
            item.sensor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.value?.toString().includes(searchTerm) ||
            item.unit?.toLowerCase().includes(searchTerm.toLowerCase())
        );

        // Sort data
        filtered.sort((a, b) => {
            let aVal = a[sortField];
            let bVal = b[sortField];
            
            if (sortField === 'time') {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            } else if (sortField === 'value') {
                aVal = parseFloat(aVal) || 0;
                bVal = parseFloat(bVal) || 0;
            }
            
            if (sortDirection === 'asc') {
                return aVal > bVal ? 1 : -1;
            } else {
                return aVal < bVal ? 1 : -1;
            }
        });

        setFilteredData(filtered);
    }, [sensorData, searchTerm, sortField, sortDirection]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const exportToCSV = () => {
        if (filteredData.length === 0) {
            Swal.fire(t('no_data') || 'No Data', t('no_data_to_export') || 'No data available to export', 'info');
            return;
        }

        const csvContent = [
            ['Sensor', 'Value', 'Unit', 'Time'],
            ...filteredData.map(item => [
                item.sensor,
                item.value,
                item.unit,
                new Date(item.time).toLocaleString()
            ])
        ].map(row => row.join(',')).join('\\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `water-quality-data-${selectedPond}-${new Date().getTime()}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const getSensorIcon = (sensorName) => {
        const name = sensorName?.toLowerCase() || '';
        if (name.includes('temp')) return '🌡️';
        if (name.includes('ph')) return '🧪';
        if (name.includes('oxygen') || name.includes('do')) return '💧';
        if (name.includes('ammonia')) return '⚗️';
        return '📊';
    };

    const getSensorColor = (sensorName) => {
        const name = sensorName?.toLowerCase() || '';
        if (name.includes('temp')) return 'text-red-600 bg-red-50';
        if (name.includes('ph')) return 'text-blue-600 bg-blue-50';
        if (name.includes('oxygen') || name.includes('do')) return 'text-teal-600 bg-teal-50';
        if (name.includes('ammonia')) return 'text-purple-600 bg-purple-50';
        return 'text-gray-600 bg-gray-50';
    };

    const getMonthNames = () => {
        return [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
    };

    const getYearOptions = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = currentYear - 5; year <= currentYear + 1; year++) {
            years.push(year);
        }
        return years;
    };

    const setDatePreset = (preset) => {
        const now = new Date();
        let start, end;
        
        switch (preset) {
            case 'today':
                start = new Date(now);
                end = new Date(now);
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                break;
            case 'yesterday':
                start = new Date(now);
                end = new Date(now);
                start.setDate(start.getDate() - 1);
                end.setDate(end.getDate() - 1);
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                break;
            case 'week':
                start = new Date(now);
                end = new Date(now);
                start.setDate(start.getDate() - 7);
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                break;
            case 'month':
                start = new Date(now);
                end = new Date(now);
                start.setDate(start.getDate() - 30);
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                break;
            default:
                return;
        }
        
        setStartDate(start);
        setEndDate(end);
        
        if (selectedPond) {
            const sensorIds = selectedSensors.map(sensor => sensor.value).join(',');
            fetchSensorData(selectedPond, sensorIds || null, 1);
        }
    };

    const handleMonthChange = (monthNumber) => {
        setSelectedMonth(monthNumber);
        const startOfMonth = new Date(selectedYear, monthNumber, 1, 0, 0, 0, 0);
        const endOfMonth = new Date(selectedYear, monthNumber + 1, 0, 23, 59, 59, 999);

        setStartDate(startOfMonth);
        setEndDate(endOfMonth);
    };

    const handleYearChange = (yearNumber) => {
        setSelectedYear(yearNumber);
        const startOfMonth = new Date(yearNumber, selectedMonth, 1, 0, 0, 0, 0);
        const endOfMonth = new Date(yearNumber, selectedMonth + 1, 0, 23, 59, 59, 999);

        setStartDate(startOfMonth);
        setEndDate(endOfMonth);
    };

    return (
        <div className="container mx-auto p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full"></div>
                    <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                        <FontAwesomeIcon icon={faTable} className="text-blue-600" />
                        {t('water_quality_table_admin') || 'Water Quality Data Table'}
                    </h1>
                </div>
                <p className="text-gray-600">{t('water_quality_table_admin_description') || 'View and analyze water quality sensor data from all farms in tabular format with advanced filtering and sorting options.'}</p>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon={faFilter} className="text-gray-600" />
                    {t('filters') || 'Filters'}
                </h2>
                
                {/* District and NGO Filters */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div>
                        <label className="block text-gray-700 mb-2 font-medium">
                            {t('Filter by District') || 'Filter by District'}
                        </label>
                        <select
                            value={filterDistrict}
                            onChange={(e) => {
                                const selected = e.target.value;
                                setFilterDistrict(selected);
                                const found = districts.find(d => d.name === selected);
                                setFilterSubdistricts(found ? found.subdistricts : []);
                                setFilterSubdistrict('');
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                            <option value="">{t('All') || 'All'} {t('Districts') || 'Districts'}</option>
                            {districts.map((d) => (
                                <option key={d._id} value={d.name}>{t(d.name)}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-gray-700 mb-2 font-medium">
                            {t('Filter by Subdistrict') || 'Filter by Subdistrict'}
                        </label>
                        <select
                            value={filterSubdistrict}
                            onChange={(e) => setFilterSubdistrict(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                            disabled={!filterDistrict}
                        >
                            <option value="">{t('All') || 'All'} {t('Subdistricts') || 'Subdistricts'}</option>
                            {filterSubdistricts.map((s, i) => (
                                <option key={i} value={s}>{t(s)}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-gray-700 mb-2 font-medium">
                            {t('Filter by NGO') || 'Filter by NGO'}
                        </label>
                        <select
                            value={filterNgo}
                            onChange={(e) => {
                                setFilterNgo(e.target.value);
                                setSelectedFarm('');
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                            <option value="">{t('All') || 'All'} {t('NGOs') || 'NGOs'}</option>
                            {ngos.map((ngo) => (
                                <option key={ngo._id} value={ngo._id}>{ngo.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Farm and Pond Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div>
                        <label htmlFor="farmSelect" className="block text-gray-700 mb-2 font-medium">
                            <span className="text-red-500">*</span> {t('select_farm') || 'Select Farm'}:
                        </label>
                        <select
                            id="farmSelect"
                            value={selectedFarm}
                            onChange={handleFarmSelect}
                            className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                            <option value="">-- {t('select_farm') || 'Select Farm'} --</option>
                            {farms
                                .filter((farm) => {
                                    const matchNgo = filterNgo ? farm.ngo_id === filterNgo : true;
                                    const matchDistrict = filterDistrict ? farm.district === filterDistrict : true;
                                    const matchSubdistrict = filterSubdistrict ? farm.subdistrict === filterSubdistrict : true;
                                    return matchNgo && matchDistrict && matchSubdistrict;
                                })
                                .map((farm) => (
                                    <option key={farm._id} value={farm._id}>
                                        🏞️ {farm.name} ( {farm.location} )
                                    </option>
                                ))
                            }
                        </select>
                    </div>

                    <div>
                        <label className="block text-gray-700 mb-2 font-medium">
                            <span className="text-red-500">*</span> {t('select_pond') || 'Select Pond'}
                        </label>
                        <select
                            value={selectedPond}
                            onChange={handlePondChange}
                            className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                            <option value="">{t('select_pond') || 'Select Pond'}</option>
                            {ponds.map((pond) => (
                                <option key={pond._id} value={pond._id}>
                                    🏞️ {pond.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Sensor Selection */}
                {sensors.length > 0 && (
                    <div className="mb-4">
                        <label className="block text-gray-700 mb-2 font-medium">
                            {t('select_sensors') || 'Select Sensors'} <span className="text-sm text-gray-500">({t('optional') || 'optional'})</span>
                        </label>
                        <Select
                            isMulti
                            value={selectedSensors}
                            onChange={handleSensorChange}
                            options={sensors.map(sensor => ({
                                value: sensor._id,
                                label: sensor?.sensor_parameter_id?.sensor_name || sensor.parameter || sensor.parameterName || sensor.type || sensor.name || `Sensor ${sensor._id}`
                            }))}
                            placeholder={selectedPond ? (t('select_sensors') || 'Choose specific parameters or leave blank for all') : (t('select_pond_first') || 'Select a farm and pond first')}
                            className="basic-multi-select"
                            classNamePrefix="select"
                            isDisabled={!selectedPond}
                        />
                    </div>
                )}

                {/* Quick Date Presets */}
                <div className="mb-6">
                    <label className="block text-gray-700 font-medium mb-2">{t('quick_dates') || 'Quick Dates'}</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <button
                            onClick={() => setDatePreset('today')}
                            className="px-3 py-2 text-sm bg-green-100 hover:bg-green-200 text-gray-700 rounded shadow transition-colors"
                        >
                            {t('today') || 'Today'}
                        </button>
                        <button
                            onClick={() => setDatePreset('yesterday')}
                            className="px-3 py-2 text-sm bg-green-100 hover:bg-green-200 text-gray-700 rounded shadow transition-colors"
                        >
                            {t('yesterday') || 'Yesterday'}
                        </button>
                        <button
                            onClick={() => setDatePreset('week')}
                            className="px-3 py-2 text-sm bg-green-100 hover:bg-green-200 text-gray-700 rounded shadow transition-colors"
                        >
                            {t('seven_days') || '7 Days'}
                        </button>
                        <button
                            onClick={() => setDatePreset('month')}
                            className="px-3 py-2 text-sm bg-green-100 hover:bg-green-200 text-gray-700 rounded shadow transition-colors"
                        >
                            {t('thirty_days') || '30 Days'}
                        </button>
                    </div>
                </div>

                {/* Month Filter and Date Range */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    {/* Month Filter */}
                    <div>
                        <label className="block text-gray-700 font-medium mb-2">{t('month_filter') || 'Month Filter'}</label>
                        <div className="grid grid-cols-2 gap-3">
                            <select
                                value={selectedMonth}
                                onChange={(e) => handleMonthChange(Number(e.target.value))}
                                className="px-3 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {getMonthNames().map((month, index) => (
                                    <option key={index} value={index}>
                                        {month.substring(0, 3)}
                                    </option>
                                ))}
                            </select>
                            <select
                                value={selectedYear}
                                onChange={(e) => handleYearChange(Number(e.target.value))}
                                className="px-3 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {getYearOptions().map((year) => (
                                    <option key={year} value={year}>
                                        {year}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Custom Date Range */}
                    <div>
                        <label className="block text-gray-700 font-medium mb-2">{t('date_range') || 'Date Range'}</label>
                        <div className="grid grid-cols-2 gap-3">
                            <DatePicker
                                selected={startDate}
                                onChange={setStartDate}
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={15}
                                dateFormat="MM/dd HH:mm"
                                className="w-full px-3 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholderText={t('from_date_time') || 'From date & time'}
                            />
                            <DatePicker
                                selected={endDate}
                                onChange={setEndDate}
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={15}
                                dateFormat="MM/dd HH:mm"
                                className="w-full px-3 py-3 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholderText={t('to_date_time') || 'To date & time'}
                            />
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                        onClick={handleDateFilter}
                        disabled={!selectedPond || loading}
                        className={`
                            flex items-center justify-center py-3 px-6 rounded-lg text-white font-medium
                            transition-all duration-300 ease-in-out
                            ${!selectedPond || loading
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 shadow-lg hover:shadow-xl'
                        }
                        `}
                    >
                        <FontAwesomeIcon icon={faSearch} className="mr-2" />
                        {loading ? (
                            <span className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {t('loading') || 'Loading...'}
                            </span>
                        ) : (t('apply_filters') || 'Apply Filters')}
                    </button>

                    <button
                        onClick={resetFilters}
                        disabled={loading}
                        className={`
                            flex items-center justify-center py-3 px-6 rounded-lg font-medium
                            transition-all duration-300 ease-in-out
                            ${loading
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 hover:from-gray-200 hover:to-gray-300 hover:shadow-md'
                        }
                        `}
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                        </svg>
                        {t('reset') || 'Reset'}
                    </button>
                </div>
            </div>

            {/* Table Controls */}
            {sensorData.length > 0 && (
                <div className="bg-white rounded-lg shadow-lg p-4 mb-6">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                        {/* Search */}
                        <div className="flex items-center gap-2">
                            <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
                            <input
                                type="text"
                                placeholder={t('search_sensors') || 'Search sensors...'}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Items per page */}
                        <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">{t('items_per_page') || 'Items per page:'}</span>
                            <select
                                value={pageSize}
                                onChange={(e) => setPageSize(Number(e.target.value))}
                                className="px-3 py-2 border border-gray-300 rounded-lg"
                            >
                                {itemsPerPageOptions.map(size => (
                                    <option key={size} value={size}>{size}</option>
                                ))}
                            </select>
                        </div>

                        {/* Export */}
                        <button
                            onClick={exportToCSV}
                            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                        >
                            <FontAwesomeIcon icon={faDownload} />
                            {t('export_csv') || 'Export CSV'}
                        </button>
                    </div>
                </div>
            )}

            {/* Data Table */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
                {filteredData.length > 0 ? (
                    <>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th 
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSort('sensor')}
                                        >
                                            <div className="flex items-center gap-1">
                                                {t('sensor_type') || 'Sensor Type'}
                                                {sortField === 'sensor' && (
                                                    <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                                )}
                                            </div>
                                        </th>
                                        <th 
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSort('value')}
                                        >
                                            <div className="flex items-center gap-1">
                                                {t('value') || 'Value'}
                                                {sortField === 'value' && (
                                                    <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                                )}
                                            </div>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('unit') || 'Unit'}
                                        </th>
                                        <th 
                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                            onClick={() => handleSort('time')}
                                        >
                                            <div className="flex items-center gap-1">
                                                {t('timestamp') || 'Timestamp'}
                                                {sortField === 'time' && (
                                                    <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                                                )}
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredData.map((sensorEntry, index) => (
                                        <tr key={index} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${getSensorColor(sensorEntry.sensor)}`}>
                                                    <span className="text-lg">{getSensorIcon(sensorEntry.sensor)}</span>
                                                    <span className="font-medium">{sensorEntry.sensor}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="text-2xl font-bold text-gray-900">{sensorEntry.value}</span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="px-2 py-1 text-xs font-semibold bg-gray-100 text-gray-800 rounded-full">
                                                    {sensorEntry.unit}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <div className="flex flex-col">
                                                    <span className="font-medium">{sensorEntry.time}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                <div>
                                    <p className="text-sm text-gray-700">
                                        {t('showing_page') || 'Showing page'} <span className="font-medium">{pagination.currentPage}</span> {t('of') || 'of'}{' '}
                                        <span className="font-medium">{pagination.totalPages}</span> 
                                        {' '}({pagination.total} {t('total_results') || 'total results'})
                                        {searchTerm && <span> ({t('filtered') || 'filtered'})</span>}
                                    </p>
                                </div>
                                
                                {pagination.totalPages > 1 && (
                                    <div className="flex items-center space-x-2">
                                        {/* Previous Button */}
                                        <button
                                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                                            disabled={pagination.currentPage === 1 || loading}
                                            className="relative inline-flex items-center px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <span className="sr-only">{t('previous') || 'Previous'}</span>
                                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                            {t('previous') || 'Previous'}
                                        </button>

                                        {/* Page Numbers */}
                                        <div className="hidden sm:flex space-x-1">
                                            {getPaginationNumbers().map((page, index) => {
                                                if (page === '...') {
                                                    return (
                                                        <span
                                                            key={`ellipsis-${index}`}
                                                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                                                        >
                                                            ...
                                                        </span>
                                                    );
                                                }
                                                
                                                const isCurrentPage = page === pagination.currentPage;
                                                return (
                                                    <button
                                                        key={page}
                                                        onClick={() => handlePageChange(page)}
                                                        disabled={loading}
                                                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-colors ${
                                                            isCurrentPage
                                                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                                                    >
                                                        {page}
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {/* Mobile Page Info */}
                                        <div className="sm:hidden">
                                            <span className="text-sm text-gray-700">
                                                {t('page') || 'Page'} {pagination.currentPage} {t('of') || 'of'} {pagination.totalPages}
                                            </span>
                                        </div>

                                        {/* Next Button */}
                                        <button
                                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                                            disabled={pagination.currentPage === pagination.totalPages || loading}
                                            className="relative inline-flex items-center px-3 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {t('next') || 'Next'}
                                            <span className="sr-only">{t('next') || 'Next'}</span>
                                            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="text-center py-12">
                        <FontAwesomeIcon icon={faTable} className="text-6xl text-gray-300 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('no_sensor_data_available') || 'No sensor data available'}</h3>
                        <p className="text-gray-500">
                            {selectedPond ? (t('no_data_found_for_filters') || 'No data found for the selected filters.') : (t('select_pond_to_view_data') || 'Please select a farm and pond to view sensor data.')}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WaterQualityTableAdmin;